import { Group, type Object3D } from 'three'
import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { flushCadTextSync } from '@/plugins/cad/diagnostics/cadTextSync'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity/BaseEntity'
import type { CadClipBinding } from '@/plugins/cad/module/CADModule/object/material/CadClipBinding'
import { restoreCadClipMaterial } from '@/plugins/cad/module/CADModule/object/material/CadClipBinding'
import { MaterialManager } from '@/plugins/cad/module/CADModule/object/material/MaterialManager'
import { AngularDimensionObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/AngularDimensionObjectManager'
import { ArcObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/ArcObjectManager'
import {
  type BaseObjectManager,
  HighlightMode,
  type IUpdateOptions,
} from '@/plugins/cad/module/CADModule/object/objectManager/BaseObjectManager'
import { CircleObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/CircleObjectManager'
import { DimensionObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/DimensionObjectManager'
import { EllipseObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/EllipseObjectManager'
import { HatchObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/HatchObjectManager'
import { MLeaderObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/MLeaderObjectManager'
import { PolylineObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/PolylineObjectManager'
import { RayObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/RayObjectManager'
import { SplineObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/SplineObjectManager'
import {
  type StaticLineBatchBuildResult,
  StaticLineBatchManager,
} from '@/plugins/cad/module/CADModule/object/objectManager/StaticLineBatchManager'
import { TextObjectManager } from '@/plugins/cad/module/CADModule/object/objectManager/TextObjectManager'
import {
  collectCadEntityStats,
  formatCadEntityStats,
  shouldLogCadEntityStats,
} from '@/plugins/cad/module/CADModule/utils/cadDiagnostics'
import { updateGroupByPlane } from '@/plugins/cad/module/common/utils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { getLoggerManager } from '@/shared/logging'
import type { IPlane } from './DataManager'

const log = getLoggerManager().getLogger('cad:diagnostics')

type CadLayer = 'dynamic' | 'text'

type ObjectManagerConstructor = new (
  parent: Object3D,
  materialManager: MaterialManager,
  entityType: EntityType
) => BaseObjectManager

interface EntityManagerDefinition {
  type: EntityType
  manager: ObjectManagerConstructor
  layer?: CadLayer
}

const ENTITY_MANAGER_DEFINITIONS: readonly EntityManagerDefinition[] = [
  { type: EntityType.Polyline, manager: PolylineObjectManager },
  { type: EntityType.Arc, manager: ArcObjectManager },
  { type: EntityType.Circle, manager: CircleObjectManager },
  { type: EntityType.Spline, manager: SplineObjectManager },
  { type: EntityType.MText, manager: TextObjectManager, layer: 'text' },
  { type: EntityType.AlignDimension, manager: DimensionObjectManager, layer: 'text' },
  { type: EntityType.Ellipse, manager: EllipseObjectManager },
  { type: EntityType.Ray, manager: RayObjectManager },
  { type: EntityType.Hatch, manager: HatchObjectManager },
  { type: EntityType.MLeader, manager: MLeaderObjectManager, layer: 'text' },
  { type: EntityType.AngularDimension, manager: AngularDimensionObjectManager, layer: 'text' },
]

/**
 * CAD 对象管理器，按实体类型派发到对应子管理器，并维护场景 Group 与材质管理器。
 */
export class ObjectManager {
  private sceneGroup: Group = new Group()
  private readonly staticLayer = new Group()
  private readonly dynamicLayer = new Group()
  private readonly highlightLayer = new Group()
  private readonly textLayer = new Group()
  private readonly entityObjectManagerMap: Map<EntityType, BaseObjectManager> = new Map()
  private materialManager = new MaterialManager()
  private readonly staticLineBatchManager = new StaticLineBatchManager(
    this.staticLayer,
    this.materialManager
  )
  private readonly entities = new Map<string, IEntity>()
  private readonly hiddenEntityIds = new Set<string>()
  private readonly dynamicEntityIds = new Set<string>()
  private readonly pinnedDynamicEntityIds = new Set<string>()
  private readonly entitySourceParents = new Map<string, Object3D>()
  private staticBatchingEnabled = false
  private _batchRebuildPending = false
  private _flushingBatches = false

  constructor() {
    const updateMatrixWorld = this.sceneGroup.updateMatrixWorld.bind(this.sceneGroup)
    this.sceneGroup.updateMatrixWorld = (force) => {
      this.flushPendingUpdates()
      flushCadTextSync()
      updateMatrixWorld(force)
    }
    this.staticLayer.name = 'cad-static-layer'
    this.dynamicLayer.name = 'cad-dynamic-layer'
    this.highlightLayer.name = 'cad-highlight-layer'
    this.textLayer.name = 'cad-text-layer'
    this.sceneGroup.add(this.staticLayer, this.dynamicLayer, this.highlightLayer, this.textLayer)
    ENTITY_MANAGER_DEFINITIONS.forEach(({ type, manager, layer = 'dynamic' }) => {
      const parent = layer === 'text' ? this.textLayer : this.dynamicLayer
      this.entityObjectManagerMap.set(type, new manager(parent, this.materialManager, type))
    })
    this.sceneGroup.userData.cadLayers = {
      staticLayer: this.staticLayer,
      dynamicLayer: this.dynamicLayer,
      highlightLayer: this.highlightLayer,
      textLayer: this.textLayer,
    }
  }

  /** 释放场景 Group 与所有子管理器资源。 */
  dispose() {
    this._batchRebuildPending = false
    this.staticLineBatchManager.dispose()
    this.sceneGroup.clear()
    this.entityObjectManagerMap.forEach((manager) => {
      manager.dispose()
    })
    this.materialManager.dispose()
    this.entities.clear()
    this.hiddenEntityIds.clear()
    this.dynamicEntityIds.clear()
    this.pinnedDynamicEntityIds.clear()
    this.entitySourceParents.clear()
    this.highlightLayer.clear()
  }

  /**
   * 获取承载全部实体渲染对象的 Group。
   *
   * @returns 场景 Group
   */
  getSceneGroup(): Group {
    this.flushPendingUpdates()
    return this.sceneGroup
  }

  updateLineTypeDashScale(dashScale: number) {
    this.materialManager.updateLineTypeDashScale(dashScale)
  }

  /**
   * 根据绘图平面更新场景 Group 的朝向与位置。
   *
   * @param plane - 绘图平面
   */
  updateGroupByPlane(plane: IPlane) {
    const group = this.sceneGroup
    updateGroupByPlane(group, plane)
  }

  /**
   * 新增实体对应渲染对象。
   *
   * @param entity - 目标实体
   */
  addEntity(entity: IEntity) {
    this.addEntityState(entity)
    if (this.staticBatchingEnabled && StaticLineBatchManager.isBatchable(entity)) {
      this.rebuildStaticLineBatches()
    }
  }

  /**
   * 批量新增实体对应渲染对象。
   *
   * @param entities - 目标实体列表
   */
  addEntities(entities: IEntity[]) {
    const stats = collectCadEntityStats(entities)
    const shouldLog = shouldLogCadEntityStats(stats)
    const startedAt = shouldLog ? getLoggerManager().now() : 0
    if (shouldLog) {
      log.info(`[CADObjectManager] addEntities started, ${formatCadEntityStats(stats)}`)
    }
    let requiresBatchRebuild = false
    for (const entity of entities) {
      this.addEntityState(entity)
      if (StaticLineBatchManager.isBatchable(entity)) requiresBatchRebuild = true
    }
    if (this.staticBatchingEnabled && requiresBatchRebuild) {
      this.rebuildStaticLineBatches()
    }
    if (shouldLog) {
      log.info(
        `[CADObjectManager] addEntities completed, ${formatCadEntityStats(stats)}, elapsed=${(getLoggerManager().now() - startedAt).toFixed(1)}ms`
      )
    }
  }

  /**
   * 删除实体对应渲染对象。
   *
   * @param entity - 目标实体
   */
  deleteEntity(entity: IEntity) {
    if (this.removeEntityState(entity)) {
      this.rebuildStaticLineBatches()
    }
  }

  /**
   * 批量删除实体对应渲染对象。
   *
   * @param entities - 目标实体列表
   */
  deleteEntities(entities: IEntity[]) {
    let requiresBatchRebuild = false
    for (const entity of entities) {
      requiresBatchRebuild = this.removeEntityState(entity) || requiresBatchRebuild
    }
    if (requiresBatchRebuild) {
      this.rebuildStaticLineBatches()
    }
  }

  /** 删除所有实体的渲染对象。 */
  deleteAllEntities() {
    this._batchRebuildPending = false
    this.entityObjectManagerMap.forEach((manager) => {
      manager.deleteAllEntities()
    })
    this.entities.clear()
    this.hiddenEntityIds.clear()
    this.dynamicEntityIds.clear()
    this.pinnedDynamicEntityIds.clear()
    this.entitySourceParents.clear()
    this.highlightLayer.clear()
    this.staticLineBatchManager.dispose()
  }

  /** 将当前裁切绑定应用到全部 CAD 渲染材质；传 null 移除裁切装饰。 */
  setClipBinding(binding: CadClipBinding | null): void {
    const materials = new Set<LineMaterial>()
    this.sceneGroup.traverse((object) => {
      const material = (object as Object3D & { material?: unknown }).material
      if (!material || Array.isArray(material)) return
      materials.add(material as LineMaterial)
    })
    for (const material of materials) {
      if (binding) binding.applyToMaterial(material)
      else restoreCadClipMaterial(material)
    }
  }

  /**
   * 更新实体对应渲染对象。
   *
   * @param entity - 新实体数据
   * @param options - 更新选项，控制是否重建几何/材质
   */
  updateEntity(entity: IEntity, options?: IUpdateOptions) {
    if (this.updateEntityState(entity, options)) {
      this.rebuildStaticLineBatches()
    }
    this.syncEntitySourceVisibility(entity)
  }

  /**
   * 批量更新实体对应渲染对象。
   *
   * @param entities - 新实体数据列表
   * @param options - 更新选项，控制是否重建几何/材质
   */
  updateEntities(entities: IEntity[], options?: IUpdateOptions) {
    let requiresBatchRebuild = false
    for (const entity of entities) {
      requiresBatchRebuild = this.updateEntityState(entity, options) || requiresBatchRebuild
    }
    if (requiresBatchRebuild) this.rebuildStaticLineBatches()
    for (const entity of entities) this.syncEntitySourceVisibility(entity)
  }

  private updateEntityInternal(entity: IEntity, options?: IUpdateOptions) {
    const manager = this.entityObjectManagerMap.get(entity.type)
    manager?.updateEntity(entity, options)
    const object = manager?.getEntityObject(entity)
    if (object) object.userData.entity = entity
    this.entities.set(entity.id, entity)
    if (this.staticBatchingEnabled && this.pinnedDynamicEntityIds.has(entity.id)) {
      // 显式编辑会话中的实体留在动态层，直到编辑提交或取消。
      this.dynamicEntityIds.add(entity.id)
    }
  }

  private addEntityState(entity: IEntity): void {
    const manager = this.entityObjectManagerMap.get(entity.type)
    const object = manager?.addEntity(entity)
    if (object?.parent) this.entitySourceParents.set(entity.id, object.parent)
    this.entities.set(entity.id, entity)
  }

  private removeEntityState(entity: IEntity): boolean {
    const wasBatched =
      this.staticBatchingEnabled && this.staticLineBatchManager.getResult().ranges.has(entity.id)
    const manager = this.entityObjectManagerMap.get(entity.type)
    manager?.deleteEntity(entity)
    this.entities.delete(entity.id)
    this.hiddenEntityIds.delete(entity.id)
    this.dynamicEntityIds.delete(entity.id)
    this.pinnedDynamicEntityIds.delete(entity.id)
    this.entitySourceParents.delete(entity.id)
    return wasBatched
  }

  private updateEntityState(entity: IEntity, options?: IUpdateOptions): boolean {
    const wasDynamic = this.dynamicEntityIds.has(entity.id)
    this.updateEntityInternal(entity, options)
    return (
      this.staticBatchingEnabled &&
      !wasDynamic &&
      !this.hiddenEntityIds.has(entity.id) &&
      StaticLineBatchManager.isBatchable(entity)
    )
  }

  /**
   * 将实体置为 hover 高亮。
   *
   * @param entity - 目标实体或实体数组
   */
  hoverEntity(entity: IEntity | IEntity[]) {
    const entities = Array.isArray(entity) ? entity : [entity]
    entities.forEach((entity) => {
      const manager = this.entityObjectManagerMap.get(entity.type)
      manager?.hover(entity)
      this.syncEntitySourceVisibility(entity)
    })
  }

  /**
   * 取消实体的 hover 高亮。
   *
   * @param entity - 目标实体或实体数组
   */
  cancelHoverEntity(entity: IEntity | IEntity[]) {
    const entities = Array.isArray(entity) ? entity : [entity]
    entities.forEach((entity) => {
      const manager = this.entityObjectManagerMap.get(entity.type)
      manager?.cancelHover(entity)
      this.syncEntitySourceVisibility(entity)
    })
  }

  /**
   * 将实体置为选中高亮。
   *
   * @param entity - 目标实体或实体数组
   */
  selectEntity(entity: IEntity | IEntity[]) {
    const entities = Array.isArray(entity) ? entity : [entity]
    entities.forEach((entity) => {
      const manager = this.entityObjectManagerMap.get(entity.type)
      manager?.select(entity)
      this.syncEntitySourceVisibility(entity)
    })
  }

  /**
   * 取消实体的选中高亮。
   *
   * @param entity - 目标实体或实体数组
   */
  cancelSelectEntity(entity: IEntity | IEntity[]) {
    const entities = Array.isArray(entity) ? entity : [entity]
    entities.forEach((entity) => {
      const manager = this.entityObjectManagerMap.get(entity.type)
      manager?.cancelSelect(entity)
      this.syncEntitySourceVisibility(entity)
    })
  }

  /**
   * 获取实体对应的渲染对象。
   *
   * @param entity - 目标实体
   * @returns 渲染对象；未创建时返回 undefined
   */
  getEntityObject(entity: IEntity): Object3D | undefined {
    this.flushPendingUpdates()
    return this.findEntityObject(entity)
  }

  private findEntityObject(entity: IEntity): Object3D | undefined {
    const manager = this.entityObjectManagerMap.get(entity.type)
    return manager?.getEntityObject(entity)
  }

  /**
   * 获取材质管理器。
   *
   * @returns 材质管理器实例
   */
  getMaterialManager() {
    return this.materialManager
  }

  /** 绑定当前引擎实例的单位服务。 */
  setUnitPresentationService(service: UnitPresentationService) {
    const manager = this.entityObjectManagerMap.get(EntityType.AlignDimension)
    if (manager instanceof DimensionObjectManager) {
      manager.setUnitPresentationService(service)
    }
  }

  /** 刷新所有未覆盖的自动线性标注。 */
  refreshUnitPresentation() {
    const manager = this.entityObjectManagerMap.get(EntityType.AlignDimension)
    if (manager instanceof DimensionObjectManager) {
      manager.refreshUnitPresentation()
    }
  }

  /**
   * 设置单个实体渲染对象的可见性。
   *
   * @param entity - 目标实体
   * @param visible - 是否可见
   */
  setEntityVisible(entity: IEntity, visible: boolean) {
    if (this.setEntityVisibilityState(entity, visible)) {
      this.rebuildStaticLineBatches()
    }
    this.syncEntitySourceVisibility(entity)
  }

  /**
   * 批量设置实体渲染对象的可见性。
   *
   * @param entities - 目标实体列表
   * @param visible - 是否可见
   */
  setEntitiesVisible(entities: IEntity[], visible: boolean) {
    let requiresBatchRebuild = false
    for (const entity of entities) {
      requiresBatchRebuild = this.setEntityVisibilityState(entity, visible) || requiresBatchRebuild
    }
    if (requiresBatchRebuild) this.rebuildStaticLineBatches()
    for (const entity of entities) {
      this.syncEntitySourceVisibility(entity)
    }
  }

  /** 开关静态线批处理；关闭时恢复实体对象的原始渲染路径。 */
  setStaticBatchingEnabled(enabled: boolean): void {
    if (this.staticBatchingEnabled === enabled) return
    this.staticBatchingEnabled = enabled
    if (enabled) {
      this.rebuildStaticLineBatches()
    } else {
      this._batchRebuildPending = false
      this.staticLineBatchManager.dispose()
      this.dynamicEntityIds.clear()
      this.pinnedDynamicEntityIds.clear()
      for (const entity of this.entities.values()) {
        const object = this.findEntityObject(entity)
        if (object) {
          object.userData.cadStaticBatchHidden = false
          object.visible = !this.hiddenEntityIds.has(entity.id)
        }
      }
    }
  }

  isStaticBatchingEnabled(): boolean {
    return this.staticBatchingEnabled
  }

  getStaticBatchResult(): StaticLineBatchBuildResult {
    this.flushPendingUpdates()
    return this.staticLineBatchManager.getResult()
  }

  /** 按需获取静态批次资源统计。 */
  getStaticBatchResourceStats() {
    this.flushPendingUpdates()
    return this.staticLineBatchManager.getResourceStats()
  }

  markEntityDynamic(entity: IEntity): void {
    if (!this.entities.has(entity.id)) return
    this.pinnedDynamicEntityIds.add(entity.id)
    if (this.dynamicEntityIds.has(entity.id)) return
    this.dynamicEntityIds.add(entity.id)
    if (this.staticBatchingEnabled) {
      this.rebuildStaticLineBatches()
      this.syncEntitySourceVisibility(entity)
    }
  }

  /**
   * 开始一次实体编辑会话。
   *
   * 与普通 updateEntity 不同，编辑会话会保持实体不回到静态批次，直到调用
   * endEntityEdit；这样拖动过程不会因为 50ms 定时器反复退出/进入静态批次。
   */
  beginEntityEdit(entity: IEntity): void {
    this.markEntityDynamic(entity)
  }

  markEntityStatic(entity: IEntity): void {
    this.pinnedDynamicEntityIds.delete(entity.id)
    if (!this.dynamicEntityIds.has(entity.id)) return
    this.dynamicEntityIds.delete(entity.id)
    if (this.staticBatchingEnabled) {
      this.rebuildStaticLineBatches()
      this.syncEntitySourceVisibility(entity)
    }
  }

  /** 结束实体编辑会话，并只触发一次静态批次重建。 */
  endEntityEdit(entity: IEntity): void {
    this.markEntityStatic(entity)
  }

  private rebuildStaticLineBatches(): void {
    if (!this.staticBatchingEnabled || this._batchRebuildPending) return
    this._batchRebuildPending = true
    queueMicrotask(() => this.flushPendingUpdates())
  }

  /** 渲染、查询和微任务结束时刷新，同一轮数据修改只提交一次批次更新。 */
  flushPendingUpdates(): void {
    if (!this._batchRebuildPending || this._flushingBatches) return
    this._flushingBatches = true
    try {
      const candidates = Array.from(this.entities.values()).filter(
        (entity) => !this.hiddenEntityIds.has(entity.id) && !this.dynamicEntityIds.has(entity.id)
      )
      const result = this.staticLineBatchManager.rebuild(candidates)
      for (const entity of this.entities.values()) {
        const object = this.findEntityObject(entity)
        if (!object) continue
        const range = result.ranges.get(entity.id)
        object.userData.cadStaticBatchHidden = Boolean(range)
        object.userData.cadStaticBatchBounds = range?.bounds
        this.syncEntitySourceVisibility(entity)
      }
      this._batchRebuildPending = false
    } finally {
      this._flushingBatches = false
    }
  }

  private setEntityVisibilityState(entity: IEntity, visible: boolean): boolean {
    const wasHidden = this.hiddenEntityIds.has(entity.id)
    const nextHidden = !visible
    const changed = wasHidden !== nextHidden
    const object = this.findEntityObject(entity)
    if (object) object.visible = visible
    if (visible) this.hiddenEntityIds.delete(entity.id)
    else this.hiddenEntityIds.add(entity.id)
    return (
      changed &&
      this.staticBatchingEnabled &&
      !this.dynamicEntityIds.has(entity.id) &&
      StaticLineBatchManager.isBatchable(entity)
    )
  }

  private syncEntitySourceVisibility(entity: IEntity): void {
    const object = this.findEntityObject(entity)
    if (!object) return
    const manager = this.entityObjectManagerMap.get(entity.type)
    const mode = manager?.getEntitySelectMode(entity)
    const highlighted = mode !== undefined && mode !== HighlightMode.None
    const sourceParent = this.entitySourceParents.get(entity.id)
    if (highlighted && object.parent !== this.highlightLayer) {
      this.highlightLayer.add(object)
    } else if (!highlighted && sourceParent && object.parent !== sourceParent) {
      sourceParent.add(object)
    }
    object.visible =
      !this.hiddenEntityIds.has(entity.id) &&
      (!object.userData.cadStaticBatchHidden || this.dynamicEntityIds.has(entity.id) || highlighted)
  }
}
